import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/modules/auth'
import type { UserInfo, RoleType } from '@/api/types'

/**
 * 认证 Store
 * 
 * 管理: 会话状态、用户信息、角色、权限列表
 * 来源: 文件34(API-DB-AUTH-BOUNDARIES) + 文件45(PERMISSION-MATRIX)
 * 
 * 认证方案(Web): HttpOnly + Secure + SameSite Cookie + CSRF Token
 * GET /api/v1/me → account/角色/校区/端能力/授权版本
 */
export const useAuthStore = defineStore('auth', () => {
  // ===== State =====
  const user = ref<UserInfo | null>(null)
  const token = ref<string>('')
  const permissions = ref<Set<string>>(new Set())
  const roles = ref<RoleType[]>([])
  const campusIds = ref<string[]>([])
  const currentCampusId = ref<string>('')
  const _restored = ref(false)

  // ===== Getters =====
  const isAuthenticated = computed(() => !!user.value || !!token.value)
  const currentRole = computed<RoleType | null>(() => roles.value[0] ?? null)
  const displayName = computed(() => user.value?.name ?? '未登录')
  // B25/EMP-12：英文角色串getter已删（S3角色中文名后全部失效成死代码，权限判断统一走 hasPermission）

  // ===== Actions =====

  /**
   * 检查是否拥有指定权限
   * @param permissionId 文件45定义的26个permissionId之一
   */
  function hasPermission(permissionId: string): boolean {
    return permissions.value.has('*') || permissions.value.has(permissionId)
  }

  /**
   * 检查是否拥有任一权限
   */
  function hasAnyPermission(...permissionIds: string[]): boolean {
    return permissionIds.some((id) => hasPermission(id))
  }

  /**
   * 登录
   * POST /api/v1/auth/login (当前阶段mock)
   */
  async function login(phone: string, password: string) {
    const res = await authApi.login(phone, password)
    token.value = res.token
    user.value = res.user
    roles.value = Array.isArray(res.user.roles) ? res.user.roles : []
    permissions.value = new Set(res.user.permissions)
    campusIds.value = res.user.campusIds
    currentCampusId.value = res.user.campusIds[0] ?? ''
    _restored.value = true

    // 持久化（开发阶段用 localStorage，生产用 HttpOnly Cookie）
    localStorage.setItem('gs_token', res.token)
    localStorage.setItem('gs_user', JSON.stringify(res.user))
  }

  /**
   * 恢复会话
   * GET /api/v1/me
   */
  async function restoreSession() {
    if (_restored.value) return
    const savedToken = localStorage.getItem('gs_token')
    const savedUser = localStorage.getItem('gs_user')
    if (savedToken && savedUser) {
      try {
        token.value = savedToken
        user.value = JSON.parse(savedUser)
        roles.value = user.value!.roles
        // 权限迁移：CUSTOMER_* → STUDENT_*（兼容旧 localStorage 缓存）
        const permMigration: Record<string, string> = {
          CUSTOMER_VIEW: 'STUDENT_VIEW',
          CUSTOMER_CREATE: 'STUDENT_CREATE',
          CUSTOMER_EDIT: 'STUDENT_EDIT',
          CUSTOMER_DELETE: 'STUDENT_DELETE',
          CUSTOMER_OTHER: 'STUDENT_OTHER',
        }
        const migrated = (user.value!.permissions as string[]).map((p) => permMigration[p] ?? p)
        permissions.value = new Set(migrated)
        campusIds.value = user.value!.campusIds
        currentCampusId.value = user.value!.campusIds[0] ?? ''
        // 持久化迁移后的权限
        user.value!.permissions = migrated
        localStorage.setItem('gs_user', JSON.stringify(user.value))
      } catch {
        logout()
      }
    }
    _restored.value = true
  }

  /**
   * 退出登录
   */
  function logout() {
    user.value = null
    token.value = ''
    permissions.value = new Set()
    roles.value = []
    campusIds.value = []
    currentCampusId.value = ''
    _restored.value = false
    localStorage.removeItem('gs_token')
    localStorage.removeItem('gs_user')
  }

  /**
   * 切换当前校区上下文
   */
  function switchCampus(campusId: string) {
    if (campusIds.value.includes(campusId)) {
      currentCampusId.value = campusId
    }
  }

  return {
    // state
    user,
    token,
    permissions,
    roles,
    campusIds,
    currentCampusId,
    // getters
    isAuthenticated,
    currentRole,
    displayName,

    // actions
    hasPermission,
    hasAnyPermission,
    login,
    restoreSession,
    logout,
    switchCampus,
  }
})
